import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useState } from 'react';

import { useSearchParams, useParams } from 'next/navigation';

import {
  HotelListFiltersViewModel,
  HotelViewModel,
  PackageListHotelViewModel,
} from '@api/v3';
import { CloseIcon, FilterIconNext } from '@assets/icons';
import { FlexibleDiv } from '@components/formStyles';
import FiltersBlock, {
  EntityFilter,
} from '@components/ui/filters/FiltersBlock';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import CustomIcon from '@components/utils/CustomIcon';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import {
  FilterAndSortContentWrapper,
  FilterAndSortWrapper,
  StyledFilterDrawer,
  StyledResponsiveListingFilter,
} from '@components/views/components/listing/drawer/ResponsiveListingFilterAndSortStyle';
import ResponsiveListingSort from '@components/views/components/listing/drawer/ResponsiveListingSort';
import { clearQueryParamsFilters } from '@components/views/hotel/utils';
import { isEmptyFilters, paramsToObject } from '@components/views/utils';
import { HOTEL_SORT_DATA, HOTEL_SORT_DATA_EXPEDİA } from '@constants/constants';
import { SortInterface, useFiltersContext } from '@contexts/Filters';
import clean from '@utils/cleanObject';
import Typography from '@components/Typography';
import useRouterNext from '@hooks/useRouterNext';

import ResponsiveListingFilters from './ResponsiveListingFilters';
import { ButtonWrapper } from './styled';
interface ResponsiveListingFilterProps {
  select?: string;
  filters?: Array<HotelListFiltersViewModel>;
  isLoading?: boolean;
  isExpedia?: boolean;
  hotels?: Array<HotelViewModel> | Array<PackageListHotelViewModel>;
  hotelFilters?: EntityFilter[] | undefined;
  setIsRedirecting?: (value: boolean) => void;
  isCity?: boolean;
}
const ResponsiveListingFilter = ({
  select,
  filters: resposiveFilters,
  isLoading,
  isExpedia,
  hotelFilters,
  setIsRedirecting,
  isCity,
}: ResponsiveListingFilterProps) => {
  const featureFilter = useMemo(() => {
    return hotelFilters?.filter((item) => {
      return item?.type === 'feature';
    });
  }, [hotelFilters]);

  const [isShowFilters, setIsShowFilter] = useState<boolean>(false);
  const { filters, setFilters, sort, setSort } = useFiltersContext();
  const [sortItem, setSortItem] = useState<SortInterface>(sort);
  const [hotelName, setHotelName] = useState<string>('');
  const [onlyAvailableProperties, setOnlyAvailableProperties] =
    useState<boolean>(false);

  const [freeCancellation, setFreeCancellation] = useState<boolean>(false);

  const [selectedThemeItems, setSelectedThemeItems] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedFeatureItems, setSelectedFeatureItems] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedCategoryItems, setSelectedCategoryItems] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedPopularityItems, setSelectedPopularities] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedAccTypes, setSelectedAccTypes] = useState<
    Array<ListingFilterItemKey>
  >([]);
  const [selectedTripAdvisor, setSelectedTripAdvisor] = useState<
    string | string[]
  >('');
  const [minAccPriceValue, setMinAccPriceValue] = useState<
    number | undefined
  >();
  const [maxAccPriceValue, setMaxAccPriceValue] = useState<
    number | undefined
  >();

  const [selectedStars, setSelectedStars] = useState<Array<number>>([]);

  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>();
  const [filtersData, setFilterData] = useState<any>({});
  const { push } = useRouterNext();

  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();

  // ================================================
  // using the useEffect to fill the component on component load
  // ================================================
  useEffect(() => {
    if (!resposiveFilters) return;

    setFilterData({
      popularties:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'popularity'
        )[0]?.items || [],
      themes:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'theme'
        )[0]?.items || [],
      features:
        resposiveFilters
          ?.filter((item: HotelListFiltersViewModel) => item.type === 'feature')
          .reduce((acc, item) => {
            return acc.concat(item.items);
          }, [] as ListingFilterItemInterface[]) || [],
      categories:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'category'
        )[0]?.items || [],
      boardTypes:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'boardType'
        )[0]?.items || [],
      star:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'star'
        )[0]?.items || [],

      onlyAvailableHotels:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'status'
        )[0]?.items[0] || {},
      freeCancellation:
        resposiveFilters?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'refundable'
        )[0]?.items[0] || {},
    });

    const selectedPopularities =
      resposiveFilters
        ?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'popularity'
        )[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    const selectedRegion =
      resposiveFilters
        ?.filter((item: HotelListFiltersViewModel) => item.type === 'region')[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    const selectedRegionsKey =
      selectedRegion?.map((item: any) => item.key) || [];

    const selectedThemes =
      resposiveFilters
        ?.filter((item: HotelListFiltersViewModel) => item.type === 'theme')[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    const selectedFeatures =
      resposiveFilters
        ?.filter((item: HotelListFiltersViewModel) => item.type === 'feature')
        .reduce((acc, item) => {
          return acc.concat(
            item.items.filter(
              (subItem: ListingFilterItemInterface) => subItem.selected === true
            )
          );
        }, [] as ListingFilterItemInterface[]) || [];

    const selectedCategories =
      resposiveFilters
        ?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'category'
        )[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    const selectedBoardTypes =
      resposiveFilters
        ?.filter(
          (item: HotelListFiltersViewModel) => item.type === 'boardType'
        )[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    const freeCancellation = resposiveFilters?.filter(
      (item: HotelListFiltersViewModel) => item.type === 'refundable'
    )[0]?.items[0]?.selected;

    const onlyAvailableProperties = resposiveFilters?.filter(
      (item: HotelListFiltersViewModel) => item.type === 'status'
    )[0]?.items[0]?.selected;

    const stars =
      resposiveFilters
        ?.filter((item: HotelListFiltersViewModel) => item.type === 'star')[0]
        ?.items.filter(
          (item: ListingFilterItemInterface) => item.selected === true
        ) || [];

    setFreeCancellation(freeCancellation);
    setOnlyAvailableProperties(onlyAvailableProperties);
    setSelectedStars(stars?.map(({ key }) => key));
    setSelectedPopularities(selectedPopularities?.map(({ key }) => key));
    setSelectedThemeItems(selectedThemes?.map(({ key }) => key));
    setSelectedFeatureItems(selectedFeatures?.map(({ key }) => key));

    setSelectedCategoryItems(selectedCategories?.map(({ key }) => key));
    setSelectedAccTypes(selectedBoardTypes?.map(({ key }) => key));
    setSelectedTripAdvisor(query?.point || '');

    // Fill the filters with the data that we get from the backend
    setFilters((prevFilters) => ({
      ...prevFilters,
      freeCancellation,
      onlyAvailableProperties,
      selectedStars,
      selectedThemeItems: selectedThemes,
      selectedFeatureItems: selectedFeatures,
      selectedCategoryItems: selectedCategories,
      selectedPopularityItems: selectedPopularities,
      selectedAccommodationTypes: selectedBoardTypes,
      selectedRegions: selectedRegionsKey,
    }));
  }, [resposiveFilters]);
  useEffect(() => {
    setMinAccPriceValue(parseInt(query?.priceMin?.toString() ?? ''));
    setMaxAccPriceValue(parseInt(query?.priceMax?.toString() ?? ''));
  }, [query]);

  // ================================================
  // Handle Check region filter items
  // ================================================
  const onRegionCheck = (checkedKeysValue: React.Key[]): void => {
    setCheckedKeys(checkedKeysValue);
  };

  // ================================================
  // Handel Sort list items click
  // ================================================
  const handleSortTabClick = (activeKey: string): void => {
    const item = HOTEL_SORT_DATA?.find((item) => item.id === activeKey);
    if (item) {
      setSort(item);
    }
  };

  const handleSelectedPopularities = (items: ListingFilterItemKey[]) => {
    setSelectedPopularities(items);
  };

  const handleSelectedThemes = (items: ListingFilterItemKey[]) => {
    setSelectedThemeItems(items);
  };

  const handleSelectedFeatures = (items: ListingFilterItemKey[]) => {
    setSelectedFeatureItems(items);
  };

  const handleSelectedCategories = (items: ListingFilterItemKey[]) => {
    setSelectedCategoryItems(items);
  };

  const handleSelectedAccTypes = (items: ListingFilterItemKey[]) => {
    setSelectedAccTypes(items);
  };
  const handleSelectedTripAdvisor = (i: string) => {
    setSelectedTripAdvisor(i);
  };

  const handleApplyClicked = (): void => {
    const { ...nextQuery } = query;
    setIsRedirecting?.(true);
    push({
      pathname: `/${params?.slug}`,
      query: clean({
        ...nextQuery,
        theme: selectedThemeItems,
        feature: selectedFeatureItems,
        category: selectedCategoryItems,
        popularity: selectedFeatureItems,
        star: selectedStars,
        boardType: selectedAccTypes,
        point: selectedTripAdvisor,
        region: checkedKeys,
        status: onlyAvailableProperties,
        refundable: freeCancellation,
        keywords: hotelName,
        sort: sort.name,
        priceMin: minAccPriceValue,
        priceMax: maxAccPriceValue,
      }),
    });
    setSortItem(sort);
    setIsShowFilter(false);
  };

  const handleStarFilter = (items: number[]) => {
    setSelectedStars(items);
  };

  const handleCloseModal = (): void => {
    setCheckedKeys(filters.selectedRegions?.map((item) => item));
    setSort(sortItem);
    setIsShowFilter(false);
    setSelectedPopularities(
      filters.selectedPopularitiesItems?.map(({ key }) => key)
    );
    setHotelName('');
    setSelectedThemeItems(filters.selectedThemeItems?.map(({ key }) => key));
    setSelectedFeatureItems(
      filters.selectedFeaturesItems?.map(({ key }) => key)
    );
    setSelectedCategoryItems(
      filters.selectedCategoryItems?.map(({ key }) => key)
    );

    setSelectedAccTypes(
      filters.selectedAccommodationTypes?.map(({ key }) => key) ?? []
    );
    setSelectedTripAdvisor(filters.selectedTripAdvisorItem || '');
    setFreeCancellation(!!filters.freeCancellation);
    setOnlyAvailableProperties(!!filters.onlyAvailableProperties);
  };

  const toggleFilter = () => {
    setIsShowFilter((s) => !s);
  };

  // ==============================================
  // Fill up the sortBy filed from the query if found
  // ==============================================
  useEffect(() => {
    if (query.sort) {
      const targetSort = HOTEL_SORT_DATA?.find(
        (item) => item.name === (query.sort as string)
      );
      if (targetSort) setSort(targetSort);
    }
  }, [query]);

  // =====================================
  // Handle clear all filters
  // =====================================
  const handleClearAllFilters = () => {
    // Clear Local Filters
    setCheckedKeys([]);
    setSort(sortItem);
    setHotelName('');
    setSelectedTripAdvisor('');
    setSelectedStars([]);
    setSelectedThemeItems([]);
    setSelectedFeatureItems([]);
    setSelectedCategoryItems([]);
    setSelectedPopularities([]);
    setSelectedAccTypes([]);
    setFreeCancellation(false);
    setOnlyAvailableProperties(false);
    setMinAccPriceValue(undefined);
    setMaxAccPriceValue(undefined);

    // Clear Filter Context
    setFilters((prevFilters) => ({
      ...prevFilters,
      freeCancellation: false,
      onlyAvailableProperties: false,
      selectedThemeItems: [],
      selectedFeaturesItems: [],
      selectedCategoryItems: [],
      selectedPopularities: [],
      selectedAccommodationTypes: [],
      selectedRegions: [],
      maxAccommodationPrice: undefined,
      minAccommodationPrice: undefined,
      selectedDateItems: [],
      hotelName: '',
      keywords: '',
    }));

    // Clear Query Params from Filters
    const nextQuery = clearQueryParamsFilters(query);
    push({
      pathname: `/${params?.slug}`,
      query: nextQuery,
    });
    setIsRedirecting?.(true);
    // Close the Filter Modal
    setIsShowFilter(false);
  };

  return (
    <StyledResponsiveListingFilter>
      {isShowFilters ? (
        <StyledFilterDrawer
          title='Sırala ve Filtrele'
          width='100%'
          height='100%'
          placement='top'
          visible={isShowFilters}
          onClose={handleCloseModal}
          closeIcon={
            <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
          }
        >
          <FilterAndSortContentWrapper>
            <FilterAndSortWrapper>
              <ResponsiveListingSort
                select={select}
                data={
                  isExpedia || isLoading
                    ? HOTEL_SORT_DATA_EXPEDİA
                    : HOTEL_SORT_DATA
                }
                handleTabClick={handleSortTabClick}
                activeKey={sort.id}
                collapsable={false}
              />
              <FlexibleDiv top={20} />
              <FiltersBlock
                applicableFilters={resposiveFilters}
                basePath='hotel-listing'
                noWrapper
              >
                {() => {
                  return (
                    <ResponsiveListingFilters
                      isFiltersModalVisible={isShowFilters}
                      hotelFilters={hotelFilters}
                      dataRegions={filters.regions}
                      onRegionCheck={onRegionCheck}
                      selectedRegions={checkedKeys}
                      hotelName={hotelName}
                      setHotelName={setHotelName}
                      isAvailableProperty={onlyAvailableProperties}
                      isAvailablePropertyCount={
                        filtersData?.onlyAvailableHotels?.count
                      }
                      handleAvailableProperty={(e) =>
                        setOnlyAvailableProperties(e)
                      }
                      freeCancellation={freeCancellation}
                      freeCancellationCount={
                        filtersData?.freeCancellation?.count
                      }
                      handleFreeCancellation={(e) => setFreeCancellation(e)}
                      dataThemes={filtersData.themes}
                      dataFeatures={filtersData.features}
                      dataCategories={filtersData.categories}
                      dataPopularities={filtersData.popularities}
                      selectedPopularityItems={selectedPopularityItems || []}
                      handlePopularitiesChecked={handleSelectedPopularities}
                      selectedThemeItems={selectedThemeItems || []}
                      selectedFeatureItems={selectedFeatureItems || []}
                      selectedCategoryItems={selectedCategoryItems || []}
                      handleThemesChecked={handleSelectedThemes}
                      handleFeaturesChecked={handleSelectedFeatures}
                      featureFilter={featureFilter}
                      handleCategoriesChecked={handleSelectedCategories}
                      dataAccTypes={filtersData.boardTypes}
                      selectedAccTypes={selectedAccTypes}
                      handleAccTypesChecked={handleSelectedAccTypes}
                      selectedTripAdvisor={selectedTripAdvisor}
                      handleSelectedTripAdvisor={handleSelectedTripAdvisor}
                      selectedStars={selectedStars}
                      handleStarFilter={handleStarFilter}
                      minValue={minAccPriceValue}
                      maxValue={maxAccPriceValue}
                      setMinValue={setMinAccPriceValue}
                      setMaxValue={setMaxAccPriceValue}
                      clearAllFilters={handleClearAllFilters}
                      disableClearAllFilters={
                        isLoading || isEmptyFilters(query)
                      }
                      hotelType={isExpedia ? 'expedia' : 'normal'}
                      isCity={isCity}
                    />
                  );
                }}
              </FiltersBlock>
            </FilterAndSortWrapper>
            <ResponsiveApplyButton
              title='Uygula'
              onClick={handleApplyClicked}
            />
          </FilterAndSortContentWrapper>
        </StyledFilterDrawer>
      ) : null}
      <ButtonWrapper onClick={toggleFilter} disabled={isLoading}>
        <CustomIcon icon={FilterIconNext} fontSize={16} paletteColor='NL800' />
        <Typography variant='caption3' weight='Regular' color='NL800'>
          Filtrele Sırala
        </Typography>
      </ButtonWrapper>
    </StyledResponsiveListingFilter>
  );
};

export default ResponsiveListingFilter;
